import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import Logo from "../../assets/rxultimate.png";
import LoginImage from "../../assets/3836182.jpg";
import axios from "axios";
import { api_url } from "../../utils/data";
import { toast } from "react-toastify";
import { RotatingLines } from "react-loader-spinner";
import { IoArrowBackOutline } from "react-icons/io5";

const VerifyResetCode = () => {
  const { token: urlParamToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse query parameters from URL
  const queryParams = new URLSearchParams(location.search);
  const queryToken = queryParams.get('token');
  const queryEmail = queryParams.get('email');
  
  // Determine which reset method we're using
  const isEmailReset = !!queryToken && !!queryEmail;
  const isPhoneReset = !!urlParamToken && !isEmailReset;
  
  // Set token based on method
  const token = isEmailReset ? queryToken : urlParamToken;
  
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [email, setEmail] = useState(queryEmail || "");

  // Function to handle verification code input
  const handleCodeChange = (e) => {
    setVerificationCode(e.target.value);
  };

  // Function to handle password input
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    
    // Clear error if the field is non-empty
    if (e.target.value) {
      setPasswordError("");
    }
  };

  // Function to handle confirm password input
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    
    // Clear error if passwords now match
    if (e.target.value === password) {
      setPasswordError("");
    }
  };

  // Function to validate the form
  const validateForm = () => {
    if (password !== confirmPassword) {
      setPasswordError("Passwords don't match");
      return false;
    }
    
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }
    
    return true;
  };

  // Function to handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    // Different request structure based on reset method
    let requestData;
    let endpoint;
    
    if (isEmailReset) {
      // Email-based reset with token
      requestData = {
        token: token,
        email: email,
        new_password: password
      };
      endpoint = `${api_url}/accounts/reset-password/confirm/`;
    } else {
      // Phone-based reset with verification code
      requestData = {
        token: token,
        verification_code: verificationCode,
        new_password: password
      };
      endpoint = `${api_url}/accounts/reset-password/confirm/`;
    }

    const body = JSON.stringify(requestData);

    axios
      .post(endpoint, body, config)
      .then((response) => {
        toast.success("Password reset successful");
        setIsLoading(false);
        navigate("/", { state: { passwordReset: true } });
      })
      .catch((error) => {
        setIsLoading(false);
        if(error?.response?.status !== 400){
            toast.error("Something unexpected happened. Please try again later.");
            return;
        } 
        if (error?.response?.data) {
          for (const property in error.response.data) {
            if (property !== "detail") {
              toast.error(`${property}: ${error.response.data[property]}`);
            } else {
              toast.error(`${error.response.data[property]}`);
            }
          }
        } else {
          toast.error("An error occurred. Please try again later.");
        }
      });
  };

  return (
    <div className="min-h-screen w-full bg-white">
      <div className="flex flex-col md:flex-row h-full min-h-screen">
        {/* Form Section */}
        <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12">
          <div className="w-full max-w-md">
            <form onSubmit={handleSubmit} className="w-full">
              <div className="mb-8">
                <div className="flex justify-center md:justify-start mb-8">
                  <img
                    src={Logo}
                    alt="rxultimate"
                    className="h-16 md:h-20 w-auto"
                  />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-xl md:text-2xl font-semibold text-[#526484] mb-2">
                    {isEmailReset ? "Set New Password" : "Verify Code & Set New Password"}
                  </h3>
                  <p className="text-[#526484] text-sm">
                    {isEmailReset 
                      ? "Create a new password for your account."
                      : "Enter the verification code sent to your phone and choose a new password."}
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                {/* Email display for email reset */}
                {isEmailReset && (
                  <div className="flex flex-col">
                    <label
                      className="text-[#344357] font-medium text-sm mb-1.5"
                      htmlFor="email"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] bg-gray-100 focus:outline-none transition-all"
                      name="email"
                      id="email"
                      value={email}
                      readOnly
                      disabled
                    />
                  </div>
                )}

                {/* Verification Code Input - only for phone reset */}
                {isPhoneReset && (
                  <div className="flex flex-col">
                    <label
                      className="text-[#344357] font-medium text-sm mb-1.5"
                      htmlFor="verificationCode"
                    >
                      Verification Code
                    </label>
                    <input
                      type="text"
                      className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      name="verificationCode"
                      id="verificationCode"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={handleCodeChange}
                      maxLength={6}
                      pattern="[0-9]{6}"
                      required={isPhoneReset}
                    />
                  </div>
                )}

                {/* New Password Input */}
                <div className="flex flex-col">
                  <label
                    className="text-[#344357] font-medium text-sm mb-1.5"
                    htmlFor="password"
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    className={`py-3 px-4 text-base rounded-lg border ${
                      passwordError ? "border-red-500" : "border-[#dbdfea]"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    name="password"
                    id="password"
                    placeholder="Create new password"
                    value={password}
                    onChange={handlePasswordChange}
                    minLength={8}
                    required
                  />
                </div>

                {/* Confirm Password Input */}
                <div className="flex flex-col">
                  <label
                    className="text-[#344357] font-medium text-sm mb-1.5"
                    htmlFor="confirmPassword"
                  >
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    className={`py-3 px-4 text-base rounded-lg border ${
                      passwordError ? "border-red-500" : "border-[#dbdfea]"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    name="confirmPassword"
                    id="confirmPassword"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    required
                  />
                  {passwordError && (
                    <p className="text-red-500 text-sm mt-1">{passwordError}</p>
                  )}
                </div>

                <div className="pt-3">
                  {isLoading ? (
                    <div className="flex justify-center items-center py-2">
                      <RotatingLines
                        visible={true}
                        height="48"
                        width="48"
                        strokeColor="blue"
                        strokeWidth="5"
                        animationDuration="0.75"
                        ariaLabel="rotating-lines-loading"
                      />
                    </div>
                  ) : (
                    <button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                      Reset Password
                    </button>
                  )}
                </div>

                <div className="flex flex-col space-y-3 pt-4">
                  {isPhoneReset && (
                    <Link
                      to="/reset-password"
                      className="text-center text-gray-700 hover:text-gray-900 text-sm"
                    >
                      Didn't receive a code? Request again
                    </Link>
                  )}
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center text-blue-600 hover:text-blue-800 font-medium text-sm"
                  >
                    <IoArrowBackOutline className="mr-1" /> Back to Login
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Image Section - Hidden on small screens */}
        <div className="hidden md:flex md:w-1/2 bg-[#f5f6fa] items-center justify-center">
          <img
            src={LoginImage}
            alt="Reset password illustration"
            className="w-4/5 max-w-lg object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default VerifyResetCode;